import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

const STORAGE_KEY = 'mo-sidebar-expanded';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  isActive: (pathname: string) => boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    label: 'Triage Queues',
    items: [
      {
        to: '/',
        label: 'Missing Orders',
        isActive: (p) => p === '/' || p.startsWith('/batch/'),
        icon: (
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m21 7.5-9-5.25L3 7.5m18 0-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
            />
          </svg>
        ),
      },
      {
        to: '/ship-memos',
        label: 'Missing Ship Memos',
        isActive: (p) => p.startsWith('/ship-memos'),
        icon: (
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
            />
          </svg>
        ),
      },
      {
        to: '/fulfillment-sync',
        label: 'Fulfillment Sync Failures',
        isActive: (p) => p.startsWith('/fulfillment-sync'),
        icon: (
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
            />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Decision Lists',
    items: [
      {
        to: '/refund-forecast',
        label: 'Refund Forecast',
        isActive: (p) => p.startsWith('/refund-forecast'),
        icon: (
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941"
            />
          </svg>
        ),
      },
      {
        to: '/stale-orders',
        label: 'Stale OMS Orders',
        isActive: (p) => p.startsWith('/stale-orders'),
        icon: (
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
            />
          </svg>
        ),
      },
      {
        to: '/blocked-refunds',
        label: 'Blocked Refunds',
        isActive: (p) => p.startsWith('/blocked-refunds'),
        icon: (
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8.25 9.75h4.875a2.625 2.625 0 0 1 0 5.25H12M8.25 9.75 10.5 7.5M8.25 9.75 10.5 12m9-7.243V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0c1.1.128 1.907 1.077 1.907 2.185Z"
            />
          </svg>
        ),
      },
    ],
  },
];

export function Sidebar() {
  const location = useLocation();
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // ignore storage failures (e.g. private mode)
      }
      return next;
    });
  };

  return (
    <aside
      className={`bg-white border-r border-gray-200 flex flex-col transition-all duration-200 ease-in-out ${
        expanded ? 'w-56' : 'w-16'
      }`}
    >
      <div className="h-14 flex items-center border-b border-gray-200 px-3">
        <button
          onClick={toggle}
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-expanded={expanded}
          className="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
            />
          </svg>
        </button>
      </div>

      <nav className="flex-1 py-3 px-2">
        {navSections.map((section, idx) => (
          <div key={section.label} className={idx > 0 ? 'mt-4' : ''}>
            {expanded ? (
              <p className="px-2 mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                {section.label}
              </p>
            ) : (
              idx > 0 && <div className="mx-2 mb-2 border-t border-gray-100" />
            )}
            <div className="space-y-1">
              {section.items.map((item) => {
                const active = item.isActive(location.pathname);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    title={!expanded ? item.label : undefined}
                    className={`flex items-center rounded-md px-2 py-2 text-sm font-medium ${
                      expanded ? '' : 'justify-center'
                    } ${
                      active
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex-shrink-0">{item.icon}</span>
                    {expanded && (
                      <span className="ml-3 whitespace-nowrap">{item.label}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}
